import { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui';
import { ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { profileToFillableFields } from '@scholarship-app/shared';
import type { Profile, Applicant } from '@scholarship-app/types';

// =============================================================================
// AutofillExportPage (v0.2.39)
//
// A small bridge page. When the autofill extension opens it (in a tab), this
// page — running authenticated as the logged-in user — loads every applicant's
// profile, flattens each to fillable fields, and posts the bundle via
// window.postMessage. The extension's content script (injected here too)
// relays it to the extension's in-memory cache.
//
// This page sends data OUT to the extension; it never persists anything new.
// It only runs for an authenticated user, so it can only ever export the
// caller's own household data.
// =============================================================================

type State =
  | { kind: 'loading' }
  | { kind: 'sent'; count: number }
  | { kind: 'error'; message: string }
  | { kind: 'unauthenticated' };

export function AutofillExportPage() {
  const { user, membership } = useAuth();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!user) { setState({ kind: 'unauthenticated' }); return; }
      if (!db || !membership) { setState({ kind: 'loading' }); return; }
      try {
        const hid = membership.householdId;
        // Load applicants.
        const appSnap = await getDocs(collection(db, 'households', hid, 'applicants'));
        const applicants = appSnap.docs
          .map((d) => d.data() as Applicant)
          .filter((a) => !a.archived);

        // Load each applicant's profile and flatten it.
        const fieldsByApplicant: Record<string, ReturnType<typeof profileToFillableFields>> = {};
        for (const a of applicants) {
          const pRef = doc(db, 'households', hid, 'applicants', a.applicant_id, 'profile', 'profile');
          const pSnap = await getDoc(pRef);
          fieldsByApplicant[a.applicant_id] = pSnap.exists()
            ? profileToFillableFields(pSnap.data() as Profile)
            : [];
        }
        if (cancelled) return;

        // Post the bundle for the extension's content script to relay.
        window.postMessage(
          {
            __scholarshipAutofill: true,
            kind: 'AUTOFILL_PROFILE_EXPORT',
            payload: {
              applicants: applicants.map((a) => ({
                applicant_id: a.applicant_id,
                display_name: a.display_name,
              })),
              fieldsByApplicant,
            },
          },
          window.location.origin
        );
        setState({ kind: 'sent', count: applicants.length });
      } catch (e) {
        if (!cancelled) {
          setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [user, membership]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <Card className="max-w-md w-full p-6 text-center space-y-3">
        {state.kind === 'loading' && (
          <>
            <Loader2 className="mx-auto animate-spin text-forest-600" size={28} />
            <p className="text-sm text-soft">Preparing your profile for autofill…</p>
          </>
        )}
        {state.kind === 'sent' && (
          <>
            <ShieldCheck className="mx-auto text-emerald-600" size={28} />
            <h1 className="font-semibold text-ink">Ready for autofill</h1>
            <p className="text-sm text-soft">
              Profile data for {state.count} applicant{state.count === 1 ? '' : 's'} has
              been handed to the extension for this session. You can close this tab —
              the data is held only in memory and isn't saved anywhere.
            </p>
          </>
        )}
        {state.kind === 'unauthenticated' && (
          <>
            <AlertCircle className="mx-auto text-amber-500" size={28} />
            <h1 className="font-semibold text-ink">Sign in first</h1>
            <p className="text-sm text-soft">
              You need to be signed in for the autofill extension to access your
              profile. Sign in, then reopen autofill from the extension.
            </p>
          </>
        )}
        {state.kind === 'error' && (
          <>
            <AlertCircle className="mx-auto text-rose-500" size={28} />
            <h1 className="font-semibold text-ink">Couldn't prepare autofill</h1>
            <p className="text-sm text-soft">{state.message}</p>
          </>
        )}
      </Card>
    </div>
  );
}
